import React, { useState, useEffect } from 'react';
import withRoleAccess from '../../hoc/withRoleAccess';
import {
  Card,
  Row,
  Col,
  DatePicker,
  Button,
  Statistic,
  Spin,
  Alert,
  Typography,
  Space,
  Table,
  Select,
  message,
  Avatar,
  Badge,
  Modal,
  Descriptions
} from 'antd';
import {
  DollarOutlined,
  ClockCircleOutlined,
  ReloadOutlined,
  CalendarOutlined,
  FilePdfOutlined,
  UserOutlined,
  TeamOutlined,
  TrophyOutlined,
  EyeOutlined,
  CalculatorOutlined,
  RiseOutlined,
  StarOutlined
} from '@ant-design/icons';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';

// Extend dayjs with isBetween plugin
dayjs.extend(isBetween);

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;
const { Option } = Select;

const API_URL = process.env.REACT_APP_API_URL;

const TeacherPaymentReport = () => {
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(30, 'day'),
    dayjs()
  ]);
  
  const [paymentsData, setPaymentsData] = useState([]);
  const [batchesData, setBatchesData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedBatch, setSelectedBatch] = useState('all');
  const [paymentDetailModal, setPaymentDetailModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);

  // Real API calls using fetch
  const fetchBatchesData = async () => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}/report/batches`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!response.ok) throw new Error('Failed to fetch batches');
    return await response.json();
  };

  const fetchPaymentData = async (batchId) => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}/report/teacher-payment/${batchId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!response.ok) throw new Error(`Failed to fetch payments for batch ${batchId}`);
    return await response.json();
  };

  const loadData = async () => {
    if (!API_URL) {
      setError('API URL is not configured. Please set REACT_APP_API_URL in your environment variables.');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      setError('Authentication token not found. Please log in again.');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      // Fetch batches first
      const batches = await fetchBatchesData();
      setBatchesData(batches);
      
      // Fetch payments for all batches
      const allPayments = [];
      for (const batch of batches) {
        try {
          const payments = await fetchPaymentData(batch.batch_id);
          allPayments.push(...payments.map(payment => ({
            ...payment,
            batch_name: batch.batch_name || `Batch ${batch.batch_id.substring(0, 8)}`
          })));
        } catch (error) {
          console.warn(`Failed to fetch payments for batch ${batch.batch_id}:`, error.message);
          // Continue with other batches even if one fails
        }
      }
      
      setPaymentsData(allPayments);
      setFilteredData(allPayments);
      
      message.success(`Loaded ${allPayments.length} payment records from ${batches.length} batches`);
    } catch (err) {
      console.error('Error loading teacher payment data:', err);
      
      let errorMessage = 'Failed to load teacher payment data';
      if (err.message.includes('fetch batches')) {
        errorMessage = 'Failed to load payment batches. Please ensure the /batches endpoint is available.';
      } else if (err.status) {
        if (err.status === 401) {
          errorMessage = 'Authentication failed. Please log in again.';
        } else if (err.status === 400) {
          errorMessage = 'Invalid request parameters';
        } else {
          errorMessage = `Server error: ${err.status}`;
        }
      } else {
        errorMessage = err.message || 'An unexpected error occurred';
      }
      
      setError(errorMessage);
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Filter data based on date range and batch
  useEffect(() => {
    let filtered = paymentsData.filter(payment => {
      const paymentDate = dayjs(payment.approval_date);
      const isInDateRange = paymentDate.isBetween(dateRange[0], dateRange[1], 'day', '[]');
      const matchesBatch = selectedBatch === 'all' || payment.batch_id === selectedBatch;
      
      return isInDateRange && matchesBatch;
    });
    
    setFilteredData(filtered);
  }, [paymentsData, dateRange, selectedBatch]);

  const handleDateRangeChange = (dates) => {
    if (dates && dates.length === 2) {
      setDateRange(dates);
    }
  };

  const handleManualRefresh = () => {
    loadData();
  };

  const handlePrintPDF = () => {
    // Open a new window for printing
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    
    // Get the content to print
    const reportContainer = document.getElementById('teacher-payment-report-container');
    const printContent = reportContainer ? reportContainer.innerHTML : '';
    
    // Create the print document
    const printDocument = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Teacher Payment Report</title>
          <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/antd/5.0.0/reset.css">
          <style>
            body { 
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              margin: 0;
              padding: 20px;
              background: white;
            }
            .ant-card { 
              box-shadow: none !important; 
              border: 1px solid #d9d9d9 !important;
              margin-bottom: 16px;
              page-break-inside: avoid;
            }
            .ant-statistic-title { font-weight: bold !important; }
            .ant-statistic-content { font-size: 18px !important; }
            .ant-card-head { 
              border-bottom: 2px solid #1890ff !important; 
              background: #f0f2f5 !important;
            }
            .ant-card-head-title { 
              font-weight: bold !important; 
              color: #1890ff !important; 
            }
            .print-header { 
              display: block !important;
              text-align: center; 
              margin-bottom: 20px; 
              border-bottom: 3px solid #1890ff; 
              padding-bottom: 10px; 
            }
            .print-date-range { 
              display: block !important;
              text-align: center; 
              margin-bottom: 15px; 
              font-weight: bold; 
            }
            svg { max-width: 100% !important; height: auto !important; }
            .no-print { display: none !important; }
            @page { 
              margin: 15mm; 
              size: A4; 
            }
          </style>
        </head>
        <body>
          ${printContent}
          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() {
                window.close();
              };
            };
          </script>
        </body>
      </html>
    `;
    
    // Write the document and trigger print
    printWindow.document.write(printDocument);
    printWindow.document.close();
  };

  const showPaymentDetails = (payment) => {
    setSelectedPayment(payment);
    setPaymentDetailModal(true);
  };

  // Calculate analytics
  const totalPayments = filteredData.reduce((sum, payment) => sum + parseFloat(payment.total_amount || 0), 0);
  const totalHours = filteredData.reduce((sum, payment) => sum + parseFloat(payment.total_hours || 0), 0);
  const totalTeachers = new Set(filteredData.map(payment => payment.teacher_name)).size;
  const averageHourlyRate = totalHours > 0 ? totalPayments / totalHours : 0;

  // Teacher breakdown
  const teacherBreakdown = filteredData.reduce((acc, payment) => {
    const teacher = payment.teacher_name;
    if (!acc[teacher]) {
      acc[teacher] = { totalAmount: 0, totalHours: 0, payments: 0 };
    }
    acc[teacher].totalAmount += parseFloat(payment.total_amount || 0);
    acc[teacher].totalHours += parseFloat(payment.total_hours || 0);
    acc[teacher].payments += 1;
    return acc;
  }, {});

  const teacherData = Object.entries(teacherBreakdown).map(([teacher, data]) => ({
    name: teacher,
    amount: data.totalAmount,
    hours: data.totalHours,
    payments: data.payments,
    hourlyRate: data.totalHours > 0 ? data.totalAmount / data.totalHours : 0
  })).sort((a, b) => b.amount - a.amount);

  // Monthly trends
  const monthlyData = filteredData.reduce((acc, payment) => {
    const month = dayjs(payment.approval_date).format('YYYY-MM');
    if (!acc[month]) {
      acc[month] = { amount: 0, hours: 0, teachers: new Set() };
    }
    acc[month].amount += parseFloat(payment.total_amount || 0);
    acc[month].hours += parseFloat(payment.total_hours || 0);
    acc[month].teachers.add(payment.teacher_name);
    return acc;
  }, {});

  const trendsData = Object.entries(monthlyData)
    .map(([month, data]) => ({
      month: dayjs(month).format('MMM YYYY'),
      amount: data.amount,
      hours: data.hours,
      teachers: data.teachers.size
    }))
    .sort((a, b) => dayjs(a.month).valueOf() - dayjs(b.month).valueOf());


  // Table columns
  const paymentColumns = [
    {
      title: 'Rank',
      dataIndex: 'rank',
      key: 'rank',
      width: 70,
      render: (_, __, index) => {
        if (index === 0) {
          return <TrophyOutlined style={{ color: '#faad14', fontSize: '18px' }} />;
        } else if (index === 1) {
          return <Badge count={2} style={{ backgroundColor: '#c0c0c0' }} />;
        } else if (index === 2) {
          return <Badge count={3} style={{ backgroundColor: '#cd7f32' }} />;
        }
        return index + 1;
      }
    },
    {
      title: 'Teacher',
      dataIndex: 'name',
      key: 'name',
      render: (name, record, index) => (
        <Space>
          <Avatar 
            icon={<UserOutlined />} 
            style={{ 
              backgroundColor: index === 0 ? '#faad14' : index === 1 ? '#c0c0c0' : index === 2 ? '#cd7f32' : '#1890ff',
              border: index < 3 ? '2px solid currentColor' : 'none'
            }} 
          />
          <Text strong={index === 0}>
            {name}
            {index === 0 && <StarOutlined style={{ color: '#faad14', marginLeft: 8 }} />}
          </Text>
        </Space>
      )
    },
    {
      title: 'Total Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount, record, index) => (
        <Text strong={index === 0} style={{ color: index === 0 ? '#faad14' : '#f5222d' }}>
          ${amount.toFixed(2)}
        </Text>
      ),
      sorter: (a, b) => a.amount - b.amount,
    },
    {
      title: 'Total Hours',
      dataIndex: 'hours',
      key: 'hours',
      render: (hours) => `${hours.toFixed(1)} hrs`,
      sorter: (a, b) => a.hours - b.hours,
    },
    {
      title: 'Payments',
      dataIndex: 'payments',
      key: 'payments',
      render: (payments) => <Badge count={payments} style={{ backgroundColor: '#52c41a' }} />,
    },
    {
      title: 'Hourly Rate',
      dataIndex: 'hourlyRate',
      key: 'hourlyRate',
      render: (rate) => `$${rate.toFixed(2)}/hr`,
      sorter: (a, b) => a.hourlyRate - b.hourlyRate,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button 
          size="small" 
          icon={<EyeOutlined />}
          onClick={() => showPaymentDetails(record)}
        >
          Details
        </Button>
      )
    }
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold">{`${label}`}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }}>
              {`${entry.dataKey}: ${entry.dataKey.includes('amount') ? '$' + entry.value.toFixed(2) : entry.value}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div style={{ padding: '24px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <div id="teacher-payment-report-container" style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Print Header - Only visible when printing */}
        <div className="print-header" style={{ display: 'none' }}>
          <Title level={2} style={{ margin: 0, color: '#1890ff' }}>
            Teacher Payment Report
          </Title>
          <Text>Generated on {new Date().toLocaleDateString()}</Text>
        </div>

        {/* Print Date Range - Only visible when printing */}
        <div className="print-date-range" style={{ display: 'none' }}>
          Report Period: {dateRange[0]?.format('YYYY-MM-DD')} to {dateRange[1]?.format('YYYY-MM-DD')}
        </div>

        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <Title level={2} style={{ marginBottom: '8px' }}>
            Teacher Payment Report
          </Title>
          <Text type="secondary">
            Comprehensive analysis of teacher compensation, hours, and payment batches
          </Text>
        </div>

        {/* Controls */}
        <Card style={{ marginBottom: '24px' }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} lg={8}>
              <Space>
                <CalendarOutlined />
                <Text strong>Date Range:</Text>
              </Space>
              <RangePicker
                value={dateRange}
                onChange={handleDateRangeChange}
                format="YYYY-MM-DD"
                allowClear={false}
                style={{ width: '100%', marginTop: '8px' }}
              />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Text strong>Payment Batch:</Text>
              <Select
                value={selectedBatch}
                onChange={setSelectedBatch}
                style={{ width: '100%', marginTop: '8px' }}
              >
                <Option value="all">All Batches</Option>
                {batchesData.map(batch => (
                  <Option key={batch.batch_id} value={batch.batch_id}>
                    {batch.batch_name || `Batch ${batch.batch_id.substring(0, 8)}`}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={24} lg={6}>
              <div style={{ marginTop: '24px' }}>
                <Space>
                  <Button 
                    type="primary" 
                    icon={<ReloadOutlined />}
                    onClick={handleManualRefresh}
                    loading={loading}
                  >
                    Refresh
                  </Button>
                  <Button 
                    type="default" 
                    icon={<FilePdfOutlined />}
                    onClick={handlePrintPDF}
                    disabled={!paymentsData.length}
                  >
                    Print PDF
                  </Button>
                </Space>
              </div>
            </Col>
          </Row>
        </Card>

        {error && (
          <Alert
            message="Error"
            description={error}
            type="error"
            showIcon
            style={{ marginBottom: '24px' }}
          />
        )}

        <Spin spinning={loading}>
          {/* Key Metrics */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Total Payments"
                  value={totalPayments}
                  precision={2}
                  prefix={<DollarOutlined />}
                  valueStyle={{ color: '#f5222d' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Total Hours"
                  value={totalHours}
                  precision={1}
                  prefix={<ClockCircleOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Teachers Paid"
                  value={totalTeachers}
                  prefix={<TeamOutlined />}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Avg Hourly Rate"
                  value={averageHourlyRate}
                  precision={2}
                  prefix={<CalculatorOutlined />}
                  valueStyle={{ color: '#722ed1' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Charts Row */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            {/* Top Earners Chart */}
            <Col xs={24} lg={12}>
              <Card 
                title={
                  <Space>
                    <TrophyOutlined style={{ color: '#faad14' }} />
                    Top Earning Teachers
                  </Space>
                }
              >
                {teacherData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={teacherData.slice(0, 8)} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                      <YAxis />
                      <RechartsTooltip content={<CustomTooltip />} />
                      <Bar dataKey="amount" fill="#faad14" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ textAlign: 'center', padding: '60px 0' }}>
                    <Text type="secondary">No payment data available</Text>
                  </div>
                )}
              </Card>
            </Col>

            {/* Monthly Payment Trends */}
            <Col xs={24} lg={12}>
              <Card 
                title={
                  <Space>
                    <RiseOutlined style={{ color: '#52c41a' }} />
                    Monthly Payment Trends
                  </Space>
                }
              >
                {trendsData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={trendsData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <RechartsTooltip content={<CustomTooltip />} />
                      <Area 
                        type="monotone" 
                        dataKey="amount" 
                        stroke="#52c41a" 
                        fill="#52c41a" 
                        fillOpacity={0.3}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ textAlign: 'center', padding: '60px 0' }}>
                    <Text type="secondary">No trend data available</Text>
                  </div>
                )}
              </Card>
            </Col>
          </Row>

          {/* Teacher Performance Table */}
          <Card 
            title={
              <Space>
                <TeamOutlined style={{ color: '#1890ff' }} />
                Teacher Payment Performance ({teacherData.length} teachers)
              </Space>
            }
          >
            <Table
              dataSource={teacherData}
              columns={paymentColumns}
              rowKey="name"
              pagination={{ 
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} teachers`
              }}
              scroll={{ x: 'max-content' }}
              summary={(pageData) => {
                const totalAmount = pageData.reduce((sum, teacher) => sum + teacher.amount, 0);
                const totalHours = pageData.reduce((sum, teacher) => sum + teacher.hours, 0);
                return (
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={2}>
                      <Text strong>Page Total:</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2}>
                      <Text strong style={{ color: '#f5222d' }}>
                        ${totalAmount.toFixed(2)}
                      </Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3}>
                      <Text strong>
                        {totalHours.toFixed(1)} hrs
                      </Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={4} colSpan={3} />
                  </Table.Summary.Row>
                );
              }}
            />
          </Card>
        </Spin>

        {/* Payment Details Modal */}
        <Modal
          title={
            <Space>
              <UserOutlined />
              Teacher Payment Details
            </Space>
          }
          open={paymentDetailModal}
          onCancel={() => setPaymentDetailModal(false)}
          footer={[
            <Button key="close" onClick={() => setPaymentDetailModal(false)}>
              Close
            </Button>
          ]}
          width={600}
        >
          {selectedPayment && (
            <Descriptions bordered column={2}>
              <Descriptions.Item label="Teacher Name" span={2}>
                <Space>
                  <Avatar icon={<UserOutlined />} />
                  <Text strong>{selectedPayment.name}</Text>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Total Amount">
                <Text strong style={{ color: '#f5222d', fontSize: '16px' }}>
                  ${selectedPayment.amount.toFixed(2)}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="Total Hours">
                <Text strong>{selectedPayment.hours.toFixed(1)} hours</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Number of Payments">
                <Badge count={selectedPayment.payments} style={{ backgroundColor: '#52c41a' }} />
              </Descriptions.Item>
              <Descriptions.Item label="Hourly Rate">
                <Text strong>${selectedPayment.hourlyRate.toFixed(2)}/hour</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Performance Rank" span={2}>
                {teacherData.findIndex(t => t.name === selectedPayment.name) === 0 ? (
                  <Space>
                    <TrophyOutlined style={{ color: '#faad14' }} />
                    <Text strong style={{ color: '#faad14' }}>Top Earner</Text>
                  </Space>
                ) : (
                  <Text>Rank #{teacherData.findIndex(t => t.name === selectedPayment.name) + 1}</Text>
                )}
              </Descriptions.Item>
            </Descriptions>
          )}
        </Modal>

        <style jsx>{`
          .print-header, .print-date-range {
            display: none;
          }
          
          @media print {
            .print-header, .print-date-range {
              display: block !important;
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default withRoleAccess(TeacherPaymentReport, ['superadmin']);